import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Building2, Mail, Lock, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../lib/api';

const AdminLogin = () => {
  const navigate = useNavigate();
  const { login, user, isAdmin } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '' });

  useEffect(() => {
    if (user && isAdmin) {
      navigate('/admin');
    }
  }, [user, isAdmin, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.password) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      const response = await authAPI.adminLogin(formData);
      login(response.data);
      toast.success('Login successful!');
      navigate('/admin');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Hero Section */}
      <div 
        className="lg:w-1/2 min-h-[50vh] lg:min-h-screen relative bg-cover bg-center"
        style={{ 
          backgroundImage: 'url(/hotel-pool.jpg)'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-primary/80 via-primary/60 to-transparent"></div>
        <div className="absolute inset-0 flex flex-col justify-center p-4 sm:p-8 lg:p-16">
          <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 sm:p-8 lg:p-10 max-w-md mx-auto lg:mx-0 shadow-2xl animate-fade-in">
            <div className="mb-4 sm:mb-6">
              <img src="/gnosis-logo.png" alt="Gnosis Hospitality" className="w-48 sm:w-56 lg:w-72 h-auto object-contain mx-auto lg:mx-0" />
            </div>
            <h1 className="font-heading text-xl sm:text-2xl lg:text-3xl font-bold text-primary mb-2 sm:mb-3 text-center lg:text-left">
              HR Admin Portal
            </h1>
            <p className="text-muted-foreground text-sm sm:text-base text-center lg:text-left">
              Manage employee surveys, analyze feedback, and drive workplace improvements.
            </p>
            <div className="mt-4 sm:mt-6 grid grid-cols-3 gap-2 sm:gap-4 text-center">
              <div>
                <p className="font-heading text-lg sm:text-2xl font-bold text-secondary">100%</p>
                <p className="text-xs text-muted-foreground">Confidential</p>
              </div>
              <div>
                <p className="font-heading text-lg sm:text-2xl font-bold text-accent">Real-time</p>
                <p className="text-xs text-muted-foreground">Analytics</p>
              </div>
              <div>
                <p className="font-heading text-lg sm:text-2xl font-bold text-primary">Easy</p>
                <p className="text-xs text-muted-foreground">Reports</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Login Form Section */}
      <div className="lg:w-1/2 flex flex-col items-center justify-center p-4 sm:p-6 lg:p-12 bg-card min-h-[50vh] lg:min-h-screen">
        <Card className="w-full max-w-md border-0 shadow-[0_8px_30px_rgb(0,0,0,0.04)] animate-scale-in">
          <CardHeader className="space-y-1 pb-4 sm:pb-6">
            <CardTitle className="font-heading text-xl sm:text-2xl text-center">
              Admin Login
            </CardTitle>
            <CardDescription className="text-center text-sm">
              Enter your credentials to access the HR dashboard
            </CardDescription>
          </CardHeader>
          <CardContent className="px-4 sm:px-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium">
                  Email Address
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    data-testid="admin-email-input"
                    placeholder="Enter email address"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="pl-10 h-12"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-sm font-medium">
                    Password
                  </Label>
                  <button
                    type="button"
                    onClick={() => navigate('/admin/forgot-password')}
                    data-testid="forgot-password-link"
                    className="text-xs text-secondary hover:text-secondary/80 transition-colors"
                  >
                    Forgot Password?
                  </button>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    data-testid="admin-password-input"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="pl-10 h-12"
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                data-testid="admin-login-btn"
                className="w-full h-12 bg-primary hover:bg-primary/90 rounded-full font-semibold"
                disabled={loading}
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <ArrowRight className="h-4 w-4 mr-2" />
                )}
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>

            <div className="mt-8 pt-6 border-t border-border">
              <p className="text-xs text-center text-muted-foreground">
                Gnosis Hospitality LLP - HR Management Portal
              </p>
            </div>

            {/* Employee Portal Link */}
            <div className="mt-4 text-center">
              <button
                onClick={() => navigate('/')}
                data-testid="employee-login-link"
                className="text-xs text-muted-foreground/50 hover:text-muted-foreground transition-colors"
              >
                Employee Portal
              </button>
            </div>
          </CardContent>
        </Card>
        <div className="mt-4 text-center" data-testid="global-footer">
          <p className="text-xs text-muted-foreground/70">Powered by HotelPulse.in | All Rights Reserved &copy; 2026</p>
          <p className="text-xs text-muted-foreground/50 mt-0.5">For support, connect at info@hotelpulse.in</p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
